// @rpg-unit/quest-journal: quests the player can read and follow (tier 4; composition framework, decision 0077), cut
// from spike 002's story (src/story/ui.ts, src/story/content.ts, the objective marker in src/story/module.ts and its
// use in src/hud/index.ts). It sits on @rpg/story's quest model (states, objectives, tracking, all saved in the story's
// own state) and gives a game:
//   - the quest source's data side: quest rows read into QuestDefs, and the standard source over them (`questSource`);
//   - the journal screen (`journalScreen`: active and finished, objectives, track a quest). Active lists the quests the
//     player accepted; quests only offered stay out unless the game sets `showOffered` (0.1.0 listed them; IMP-0032);
//   - the quest notice (`mountQuestNotice`: quest taken, objective done, quest finished or failed), fed by the
//     standard `story:quest` event through its kernel module (`createQuestJournal().module()`, which declares it needs
//     `story:quest`) and `onChange`;
//   - the tracked objective's marker (`resolveMarker`) and its map and compass markers in @rpg/ui's shapes.
// It meets other features only at `story:quest` (@rpg/contracts) and the HUD's map and compass sources.
import type { StandardEvents } from "@rpg/contracts";
import type { GameModule } from "@rpg/kernel";
import strings from "./strings.json";
import type { QuestChange } from "./notice";
import { QUEST_JOURNAL_FAULTS } from "./faults";

export { questSource, readAction, readQuests, type ActionRow, type ObjectiveRow, type QuestRow, type StringRef } from "./quests";
export { journalScreen, journalTabEntries, JOURNAL_CSS, JOURNAL_LABELS, type JournalLabels, type JournalScreenHost, type JournalTab } from "./screen";
export { QUEST_JOURNAL_FAULTS } from "./faults";
export { mountQuestNotice, noticeText, type QuestChange } from "./notice";
export { objectiveCompassItem, objectiveMapMarker, resolveMarker, type MarkerResolvers, type Vec3 } from "./markers";

/** This unit's version (the manifest's and package.json's). */
export const QUEST_JOURNAL_VERSION = "0.2.0" as const;

/** English defaults for the journal's and the notices' labels (`{ id, text }` rows for a strings table). */
export const QUEST_JOURNAL_STRINGS = strings as { id: string; text: string }[];

/** The journal's sim side: a kernel module that hears `story:quest` and hands each change to its listeners (the notice). */
export interface QuestJournal {
  /** The kernel module (`id: "quest-journal"`; needs `story:quest`). */
  module<M extends object = StandardEvents>(): GameModule<M>;
  /** Hear each quest change (as it is emitted, inside the step); returns the remover. */
  onChange(fn: (c: QuestChange) => void): () => void;
}

/** Make the journal's module and its listener list. */
export function createQuestJournal(): QuestJournal {
  const listeners = new Set<(c: QuestChange) => void>();
  return {
    module<M extends object = StandardEvents>(): GameModule<M> {
      const m: GameModule<StandardEvents> = {
        id: "quest-journal",
        version: QUEST_JOURNAL_VERSION,
        // checked declarations (decision 0077): the quest changes come from whatever provides story:quest
        needs: { events: ["story:quest"] },
        setup(ctx) {
          ctx.events.on("story:quest", (c) => {
            if (QUEST_JOURNAL_FAULTS.deaf) return;
            for (const f of listeners) f(c);
          });
        },
      };
      return m as unknown as GameModule<M>;
    },
    onChange(fn) {
      listeners.add(fn);
      return () => void listeners.delete(fn);
    },
  };
}
